import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Search,
  FolderPlus,
  PanelLeft,
  PanelLeftClose,
  Settings,
  Moon,
  Sun,
  MoreHorizontal,
  X,
  Pencil,
  Trash2,
  ChevronDown,
  Loader,
  Check,
  Folder,
  Archive,
  XCircle,
  Plus,
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useNotification } from "../context/NotificationContext";
import { SidebarChatSkeleton, SkeletonLoader } from "./SkeletonLoader";
import { fetchProjects, createProject, updateProject, archiveProject, deleteProject } from "../services/projectService";
import NewProjectModal from "./NewProjectModal";
import RenameChatDialog from "./dashboard/RenameChatDialog";
import AddToProjectDialog from "./dashboard/AddToProjectDialog";
import ProjectContextMenu from "./dashboard/ProjectContextMenu";
import RenameProjectDialog from "./dashboard/RenameProjectDialog";
import DeleteProjectDialog from "./dashboard/DeleteProjectDialog";
import ArchivedProjectsView from "./dashboard/ArchivedProjectsView";
import "./dashboard/Dashboard.css"; // Re-using Dashboard styles

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const DEBATE_LIST_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
let debateListCache = {
  userId: null,
  chats: [],
  page: 1,
  hasMore: false,
  ts: 0,
};

const DebateSidebar = ({
  sidebarOpen,
  setSidebarOpen,
  newChatFromDebate = null,
  onClearNewChat,
  headerHeightPx,
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const isLoggedIn = !!isAuthenticated;
  const { darkMode, toggleDarkMode } = useTheme();
  const { showError, showSuccess, showWarning, confirm } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  // Get chatId from URL - extract from pathname since we might not have direct route access
  const chatIdFromUrl = React.useMemo(() => {
    const match = location.pathname.match(/\/debate-mode\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);
  const activeRunSelection = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const run = (sp.get("run") || "").trim().toLowerCase();
      const rerunRaw = sp.get("rerun");
      const rerun = rerunRaw ? parseInt(rerunRaw, 10) : NaN;
      return {
        run,
        rerun: Number.isFinite(rerun) && rerun > 0 ? rerun : null,
      };
    } catch (e) {
      return { run: "", rerun: null };
    }
  }, [location.search]);

  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [debatesPage, setDebatesPage] = useState(1);
  const [debatesHasMore, setDebatesHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false); // Track if search dialog is open
  const [searchDialogLoaded, setSearchDialogLoaded] = useState(false); // Track if search dialog is loaded
  const [accountOpen, setAccountOpen] = useState(false);
  const [aiUsage, setAiUsage] = useState({
    totalTokensUsed: 0,
    tokenLimit: 2000000,
    aiActionsUsed: 0,
    aiMonthlyLimit: 0,
  });
  const [chatTitleMaxLength, setChatTitleMaxLength] = useState(40); // Dynamic based on screen width
  const [renameDialogChatId, setRenameDialogChatId] = useState(null);
  const [renameDialogAnchor, setRenameDialogAnchor] = useState(null);
  const [addToProjectDialogChatId, setAddToProjectDialogChatId] =
    useState(null);
  const [addToProjectDialogAnchor, setAddToProjectDialogAnchor] =
    useState(null);
  const [openMenuId, setOpenMenuId] = useState(null); // Track which chat menu is open
  const [menuPosition, setMenuPosition] = useState({}); // Store menu positions
  const [deletingChatId, setDeletingChatId] = useState(null); // Show "Deleting…" on this chat
  const [projects, setProjects] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const projectsDropdownRef = useRef(null);
  // Project management state (matches Research Mode)
  const [projectContextMenu, setProjectContextMenu] = useState(null); // { project, x, y }
  const [renameProjectDialog, setRenameProjectDialog] = useState(null); // project object
  const [deleteProjectTarget, setDeleteProjectTarget] = useState(null); // project object
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const searchInputRef = useRef(null); // Ref for search input
  const chatListRef = useRef(null); // Ref for chat list container
  const wasNearBottomRef = useRef(false); // Trigger load only when crossing into bottom zone
  const hasLoadedChatsOnce = useRef(false); // Avoid showing skeletons when switching between chats

  // Screen size detection
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [isMediumScreen, setIsMediumScreen] = useState(
    window.innerWidth >= 501 && window.innerWidth <= 900,
  );
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth <= 500);
  // Below 900px debate-workspace.css turns this sidebar into an off-canvas
  // drawer, and DebateMode starts it closed there. The slide-out below used to
  // key on the 500px "mobile" check instead, so between 501 and 900px — and
  // on a phone whose width was read before a device-mode or zoom change — the
  // CLOSED sidebar was still drawn at x:0 as a bare icon rail over the page,
  // with nothing to dismiss it. Same media query as the CSS, so they agree.
  const SIDEBAR_OVERLAY_QUERY = "(max-width: 900px)";
  const [isSidebarOverlay, setIsSidebarOverlay] = useState(
    () => window.matchMedia(SIDEBAR_OVERLAY_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(SIDEBAR_OVERLAY_QUERY);
    const onChange = (e) => setIsSidebarOverlay(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  const resolvedHeaderHeightPx =
    typeof headerHeightPx === "number" && headerHeightPx > 0
      ? headerHeightPx
      : isMobileScreen
        ? 50
        : 69;

  const normalizeDebateChatStatus = React.useCallback((chat) => {
    if (!chat || typeof chat !== "object") return chat;
    const next = { ...chat };
    if (next.stopped === true) {
      next.chatStatus = "stopped";
      next.rerunInProgress = false;
      return next;
    }
    if (!next.chatStatus) {
      next.chatStatus = "done";
    }
    return next;
  }, []);

  const navigateToFreshDebate = React.useCallback(() => {
    navigate("/debate-mode", {
      replace: true,
      state: { forceFreshDebate: Date.now() },
    });
  }, [navigate]);

  const focusDebateInput = React.useCallback((delayMs = 0) => {
    window.setTimeout(() => {
      const input = document.querySelector(".debate-textarea");
      input?.focus();
    }, delayMs);
  }, []);

  const handleNewDebateClick = React.useCallback(
    ({ closeSearch = false } = {}) => {
      navigateToFreshDebate();

      if (closeSearch) {
        setIsSearchOpen(false);
        setSearchQuery("");
      }

      if (isMobileScreen && sidebarOpen) {
        setSidebarOpen(false);
        focusDebateInput(320);
        return;
      }

      focusDebateInput(0);
    },
    [
      focusDebateInput,
      isMobileScreen,
      navigateToFreshDebate,
      setSidebarOpen,
      sidebarOpen,
    ],
  );

  const handleSidebarProjectsClick = React.useCallback((e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setProjectsDropdownOpen(true);
      return;
    }
    
    setProjectsDropdownOpen((o) => !o);
  }, [sidebarOpen, setSidebarOpen]);

  // Calculate max chat title length based on screen width (same logic as Dashboard)
  const calculateMaxLength = () => {
    const width = window.innerWidth;

    if (width <= 500) {
      // Very small screens: 10 characters
      return 10;
    } else if (width <= 800) {
      // Small to medium screens: 20 characters
      return 20;
    } else if (width <= 1400) {
      // Medium to large screens: 25 characters
      return 25;
    } else {
      // Extra large screens (above 1400px): 40 characters
      return 40;
    }
  };

  const formatMonthDay = (value) => {
    if (!value) return "";
    const dt =
      typeof value === "number" || typeof value === "string"
        ? new Date(value)
        : value instanceof Date
          ? value
          : new Date(value);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const getDebateTitleLabel = (chat) => {
    const rawTitle = (chat?.title || "").trim();
    if (rawTitle && rawTitle.toLowerCase() !== "new chat") return rawTitle;
    const fallbackDate = formatMonthDay(chat?.createdAt || chat?.updatedAt);
    return fallbackDate || "Untitled Debate";
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsSmallScreen(width <= 1024);
      setIsMediumScreen(width >= 501 && width <= 900);
      setIsMobileScreen(width <= 500);
      // Update chat title max length on resize
      setChatTitleMaxLength(calculateMaxLength());
    };
    window.addEventListener("resize", handleResize);
    // Set initial value
    setChatTitleMaxLength(calculateMaxLength());
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset initial-load flag when user logs out so next login shows skeletons on first load
  useEffect(() => {
    if (!user) {
      hasLoadedChatsOnce.current = false;
    }
  }, [user]);

  // Fetch debate chats (paginated, with cache for instant back-navigation)
  const fetchChats = React.useCallback(
    async (signal = null, { page = 1, limit = 8 } = {}) => {
      if (!user) return;

      const useCache =
        page === 1 &&
        debateListCache.userId === user.userId &&
        debateListCache.chats.length > 0 &&
        Date.now() - debateListCache.ts < DEBATE_LIST_CACHE_MAX_AGE_MS;
      if (useCache) {
        setChats(debateListCache.chats);
        setDebatesPage(debateListCache.page);
        setDebatesHasMore(debateListCache.hasMore);
        setLoadingChats(false);
        hasLoadedChatsOnce.current = true;
      } else if (page === 1 && !hasLoadedChatsOnce.current) {
        setLoadingChats(true);
      }

      try {
        const token = localStorage.getItem("token");
        const config = {
          withCredentials: true,
          params: { page, limit },
        };
        if (token) config.headers = { Authorization: `Bearer ${token}` };
        if (signal) config.signal = signal;

        const response = await axios.get(`${API_URL}/ai/debates`, config);
        const {
          debates: chatsData = [],
          page: currentPage = page,
          hasMore = false,
        } = response.data || {};

        if (signal?.aborted) return;

        const chatsWithIds = (chatsData || []).map((chat) => {
          const chatId = chat.chatId || chat._id;
          if (chatId && /^\d{16}$/.test(String(chatId))) {
            chat.chatId16 = chat.chatId16 || chatId;
          } else {
            chat.chatId16 = chat.chatId16 || chatId;
          }
          return normalizeDebateChatStatus(chat);
        });

        setChats(chatsWithIds);
        setDebatesPage(currentPage);
        setDebatesHasMore(hasMore);
        hasLoadedChatsOnce.current = true;
        if (page === 1 && user?.userId) {
          debateListCache = {
            userId: user.userId,
            chats: chatsWithIds,
            page: currentPage,
            hasMore,
            ts: Date.now(),
          };
        }
      } catch (error) {
        if (
          error.response?.status === 401 ||
          error.name === "AbortError" ||
          error.name === "CanceledError"
        ) {
          return;
        }
        console.error("Error fetching debate chats:", error);
      } finally {
        if (!useCache) setLoadingChats(false);
      }
    },
    [user],
  );

  const loadMoreDebates = React.useCallback(async () => {
    if (!user || loadingMore || !debatesHasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = debatesPage + 1;
      const token = localStorage.getItem("token");
      const config = {
        withCredentials: true,
        params: { page: nextPage, limit: 8 },
      };
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/ai/debates`, config);
      const {
        debates: nextChats = [],
        page: currentPage = nextPage,
        hasMore = false,
      } = response.data || {};
      const chatsWithIds = (nextChats || []).map((chat) => {
        const chatId = chat.chatId || chat._id;
        chat.chatId16 = chat.chatId16 || chatId;
        return normalizeDebateChatStatus(chat);
      });
      setChats((prev) => [...prev, ...chatsWithIds]);
      setDebatesPage(currentPage);
      setDebatesHasMore(hasMore);
    } catch (error) {
      if (
        error.response?.status === 401 ||
        error.name === "AbortError" ||
        error.name === "CanceledError"
      ) {
        return;
      }
      console.error("Error loading more debates:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [user, loadingMore, debatesHasMore, debatesPage, normalizeDebateChatStatus]);

  // Auto-load older debates when user scrolls near the bottom.
  // It only fires when crossing into the bottom zone, preventing runaway loops.
  useEffect(() => {
    if (!sidebarOpen) return;

    const listEl = chatListRef.current;
    if (!listEl) return;

    const BOTTOM_THRESHOLD_PX = 80;
    const onScroll = () => {
      if (loadingChats || loadingMore || !debatesHasMore) return;

      const distanceToBottom =
        listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
      const isNearBottom = distanceToBottom <= BOTTOM_THRESHOLD_PX;

      if (isNearBottom && !wasNearBottomRef.current) {
        loadMoreDebates();
      }

      wasNearBottomRef.current = isNearBottom;
    };

    listEl.addEventListener("scroll", onScroll, { passive: true });
    return () => listEl.removeEventListener("scroll", onScroll);
  }, [sidebarOpen, chats.length, loadingChats, loadingMore, debatesHasMore, loadMoreDebates]);

  // Keep wheel/touch scrolling scoped to the chat list so the main page
  // does not start scrolling when interacting inside the sidebar.
  useEffect(() => {
    if (!sidebarOpen) return;

    const listEl = chatListRef.current;
    if (!listEl) return;

    const EDGE_TOLERANCE_PX = 1;
    const atTop = () => listEl.scrollTop <= EDGE_TOLERANCE_PX;
    const atBottom = () =>
      listEl.scrollTop + listEl.clientHeight >=
      listEl.scrollHeight - EDGE_TOLERANCE_PX;

    const handleWheel = (event) => {
      const deltaY = event.deltaY || 0;
      if (!deltaY) return;

      const scrollingUp = deltaY < 0;
      const scrollingDown = deltaY > 0;
      if ((scrollingUp && atTop()) || (scrollingDown && atBottom())) {
        event.preventDefault();
      }
      event.stopPropagation();
    };

    let touchStartY = null;
    const handleTouchStart = (event) => {
      touchStartY = event.touches?.[0]?.clientY ?? null;
    };
    const handleTouchMove = (event) => {
      const touchY = event.touches?.[0]?.clientY;
      if (touchStartY == null || touchY == null) return;

      const deltaY = touchStartY - touchY;
      const scrollingUp = deltaY < 0;
      const scrollingDown = deltaY > 0;
      if ((scrollingUp && atTop()) || (scrollingDown && atBottom())) {
        event.preventDefault();
      }
      event.stopPropagation();
    };

    listEl.addEventListener("wheel", handleWheel, { passive: false });
    listEl.addEventListener("touchstart", handleTouchStart, { passive: true });
    listEl.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      listEl.removeEventListener("wheel", handleWheel);
      listEl.removeEventListener("touchstart", handleTouchStart);
      listEl.removeEventListener("touchmove", handleTouchMove);
    };
  }, [sidebarOpen]);

  // Auto-fill sidebar pages until list is scrollable for mobile parity with desktop.
  useEffect(() => {
    if (!sidebarOpen) return;
    if (loadingChats || loadingMore || !debatesHasMore) return;

    const listEl = chatListRef.current;
    if (!listEl) return;

    const canScroll = listEl.scrollHeight > listEl.clientHeight + 4;
    if (!canScroll) {
      loadMoreDebates();
    }
  }, [
    sidebarOpen,
    chats.length,
    loadingChats,
    loadingMore,
    debatesHasMore,
    loadMoreDebates,
  ]);

  // Reset threshold latch whenever sidebar is reopened or route changes,
  // so infinite-scroll can trigger again after cross-page navigation.
  useEffect(() => {
    wasNearBottomRef.current = false;
  }, [sidebarOpen, location.pathname]);

  // Fetch AI usage function
  const fetchAIUsage = React.useCallback(
    async (signal = null) => {
      if (!user) return;

      try {
        const token = localStorage.getItem("token");
        const config = {
          withCredentials: true,
        };
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` };
        }
        if (signal) {
          config.signal = signal;
        }

        const response = await axios.get(`${API_URL}/ai/usage`, config);
        setAiUsage(response.data);
      } catch (error) {
        if (
          error.response?.status === 401 ||
          error.name === "AbortError" ||
          error.name === "CanceledError"
        ) {
          return;
        }
        console.error("Error fetching AI usage:", error);
      }
    },
    [user],
  );

  // Refresh AI usage UI instantly after uploads complete (cross-component event).
  useEffect(() => {
    const handler = () => fetchAIUsage();
    window.addEventListener("lawfusion-ai-usage-refresh", handler);
    return () =>
      window.removeEventListener("lawfusion-ai-usage-refresh", handler);
  }, [fetchAIUsage]);

  // Add new debate chat to sidebar as soon as processing starts (from DebateMode start event)
  useEffect(() => {
    if (!newChatFromDebate || !onClearNewChat) return;
    const chatId = newChatFromDebate.chatId || newChatFromDebate._id;
    setChats((prev) => {
      const idx = prev.findIndex((c) => (c.chatId || c._id) === chatId);
      if (idx !== -1) {
        // Allow "update" events (e.g., rerun-start) to optimistically change an existing row.
        if (newChatFromDebate.sidebarEvent === "rerun-start") {
          const existing = prev[idx] || {};
          const next = [...prev];
          const currentCount = parseInt(existing.rerunCount || 0, 10) || 0;
          next[idx] = {
            ...existing,
            rerunCount: currentCount + 1,
            updatedAt: Date.now(),
            rerunInProgress: true,
            chatStatus: newChatFromDebate.chatStatus || "loading",
          };
          return next;
        }
        if (newChatFromDebate.sidebarEvent === "status-update") {
          const existing = prev[idx] || {};
          const next = [...prev];
          next[idx] = {
            ...existing,
            updatedAt: Date.now(),
            rerunInProgress:
              newChatFromDebate.chatStatus === "loading"
                ? true
                : existing.rerunInProgress && newChatFromDebate.chatStatus !== "done",
            chatStatus: newChatFromDebate.chatStatus || existing.chatStatus || "done",
          };
          return next;
        }
        return prev;
      }
      if (newChatFromDebate.sidebarEvent === "status-update") {
        return prev;
      }
      return [newChatFromDebate, ...prev];
    });
    onClearNewChat();
  }, [newChatFromDebate, onClearNewChat]);

  // Fetch debate chats, usage, and projects in parallel on mount
  useEffect(() => {
    try {
      const savedProjectId = localStorage.getItem("debateSelectedProjectId");
      if (savedProjectId) {
        setSelectedProjectId(savedProjectId);
      }
    } catch (e) {
      // ignore localStorage errors
    }

    if (user) {
      fetchChats();
      fetchAIUsage();
      // Debate sidebar should only see debate-mode projects
      fetchProjects("debate").then((list) =>
        setProjects(Array.isArray(list) ? list : []),
      );
    }
  }, [user, fetchChats, fetchAIUsage]);



  // Refresh AI usage periodically (every 30 seconds)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchAIUsage();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [user, fetchAIUsage]);

  // Filter debate chats based on search query (for sidebar display - only filter if searchQuery is set)
  // Note: When search dialog is open, we'll use the same filteredChats but display in dialog
  const filteredChats = chats.filter((chat) => {
    // Only filter if searchQuery is set (for sidebar filtering)
    // When search dialog is open, it will use this same filtered list
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const title = (chat.title || "").toLowerCase();
    const chatQuery = (chat.query || "").toLowerCase();
    let textContent = "";
    if (Array.isArray(chat.debateRounds)) {
      chat.debateRounds.forEach((debate) => {
        const args = debate.arguments;
        textContent +=
          (args?.advocate?.text || "") +
          (args?.critic?.text || "") +
          (args?.strategist?.text || "") +
          (args?.consensus?.text || "");
      });
    }

    return (
      title.includes(query) ||
      chatQuery.includes(query) ||
      textContent.includes(query)
    );
  });

  // Handle keyboard shortcuts for search dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSearchOpen) {
        if (e.key === "Escape") {
          setIsSearchOpen(false);
          setSearchQuery("");
        }
      } else if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchDialogLoaded(true);
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSearchOpen]);

  // Focus search input when dialog opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  const handleSelectProject = React.useCallback(
    (projectId) => {
      const normalizedProjectId = projectId || null;
      const isChanged = (selectedProjectId || null) !== normalizedProjectId;

      setSelectedProjectId(normalizedProjectId);
      try {
        if (normalizedProjectId) {
          localStorage.setItem("debateSelectedProjectId", normalizedProjectId);
        } else {
          localStorage.removeItem("debateSelectedProjectId");
        }
      } catch (e) {
        // ignore localStorage errors
      }

      // Switching project context should open a clean/new debate page state.
      // This prevents stale previously selected chat content from staying visible.
      if (isChanged) {
        navigate("/debate-mode", { replace: true });
      }
    },
    [selectedProjectId, navigate],
  );

  // Handle chat selection from search dialog
  const handleChatSelect = (chatId) => {
    // Find the chat to get its 16-digit ID
    const chat = chats.find((c) => (c.chatId || c._id) === chatId);
    const chatId16 = chat ? chat.chatId16 || chat.chatId || chat._id : chatId;

    // Update selected project if the chat belongs to one
    if (chat) {
      const projId = chat.projectId || null;
      setSelectedProjectId(projId);
      try {
        if (projId) {
          localStorage.setItem("debateSelectedProjectId", projId);
        } else {
          localStorage.removeItem("debateSelectedProjectId");
        }
      } catch (e) {
        // ignore
      }
    }

    // Navigate to the debate mode URL with 16-digit ID
    navigate(`/debate-mode/${chatId16}?run=initial`, { replace: true });
    if (isMobileScreen && sidebarOpen) {
      setSidebarOpen(false);
    }
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  const handleLogout = async () => {
    navigate("/login", { replace: true });
    await logout();
  };

  // Get current chat ID from URL for active state
  const currentChatId = React.useMemo(() => {
    if (!chatIdFromUrl) return null;
    // Find the chat that matches the URL chatId
    const chat = chats.find((c) => {
      const chatId16 = c.chatId16 || c.chatId || c._id;
      return (
        chatId16 === chatIdFromUrl ||
        c._id === chatIdFromUrl ||
        c.chatId === chatIdFromUrl
      );
    });
    return chat ? chat._id || chat.chatId : null;
  }, [chatIdFromUrl, chats]);

  // Calculate menu position - centered below the three dots button
  const calculateMenuPosition = React.useCallback((chatId, buttonElement) => {
    if (!buttonElement) {
      console.warn("No button element provided for menu position calculation");
      return { top: 0, left: 0, visible: false };
    }

    const buttonRect = buttonElement.getBoundingClientRect();
    const menuWidth = 130;
    const menuHeight = 90;
    const verticalPadding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const horizontalPadding = 12;

    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    let left = buttonCenterX - menuWidth / 2;
    let top = buttonRect.bottom + verticalPadding;

    if (left < horizontalPadding) {
      left = horizontalPadding;
    }
    if (left + menuWidth > viewportWidth - horizontalPadding) {
      left = viewportWidth - menuWidth - horizontalPadding;
    }
    if (top + menuHeight > viewportHeight - horizontalPadding) {
      top = buttonRect.top - menuHeight - verticalPadding;
      if (top < horizontalPadding) {
        top = viewportHeight - menuHeight - horizontalPadding;
      }
    }
    if (top < horizontalPadding) {
      top = horizontalPadding;
    }

    return { top, left, visible: true };
  }, []);

  // Handle menu toggle
  const handleMenuToggle = (chatId, e) => {
    e.stopPropagation();
    const buttonElement = e.currentTarget;

    if (openMenuId === chatId) {
      setOpenMenuId(null);
      setMenuPosition((prev) => {
        const newPos = { ...prev };
        delete newPos[chatId];
        return newPos;
      });
    } else {
      setOpenMenuId(null);
      setTimeout(() => {
        const position = calculateMenuPosition(chatId, buttonElement);
        setMenuPosition((prev) => ({ ...prev, [chatId]: position }));
        setOpenMenuId(chatId);
      }, 50);
    }
  };

  // Update menu position on scroll/resize when menu is open
  useEffect(() => {
    if (!openMenuId) return;

    const updateMenuPosition = () => {
      const buttonElement = document.querySelector(
        `[data-chat-id="${openMenuId}"]`,
      );
      if (buttonElement) {
        const position = calculateMenuPosition(openMenuId, buttonElement);
        setMenuPosition((prev) => ({ ...prev, [openMenuId]: position }));
      }
    };

    const timeoutId = setTimeout(updateMenuPosition, 10);
    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);
    const chatList = chatListRef.current;
    if (chatList) {
      chatList.addEventListener("scroll", updateMenuPosition, {
        passive: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
      if (chatList) {
        chatList.removeEventListener("scroll", updateMenuPosition);
      }
    };
  }, [openMenuId, calculateMenuPosition]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return;

    const handleClickOutside = (event) => {
      const menuElement = event.target.closest(".chat-menu-dropdown-portal");
      const buttonElement = event.target.closest("[data-chat-id]");

      if (menuElement) return;
      if (buttonElement?.getAttribute("data-chat-id") === openMenuId) return;

      setOpenMenuId(null);
      setMenuPosition((prev) => {
        const newPos = { ...prev };
        delete newPos[openMenuId];
        return newPos;
      });
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [openMenuId]);

  const renderUserAvatar = () => {
    if (user?.avatarUrl) {
      return (
        <img
          src={user.avatarUrl}
          alt="avatar"
          className="account-avatar-image"
        />
      );
    }
    return getUserInitials();
  };

  // Save debate title (used by Rename dialog)
  const saveRename = async (chatId, newTitle) => {
    const trimmed = (newTitle || "").trim();
    if (!trimmed) {
      showWarning("Title cannot be empty");
      return;
    }
    const chat = chats.find((c) => c._id === chatId || c.chatId === chatId);
    if (!chat) {
      showError("Debate not found");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const config = { withCredentials: true };
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const chatIdToUpdate = chat.chatId || chat._id;
      await axios.put(
        `${API_URL}/ai/chats/${chatIdToUpdate}`,
        { title: trimmed },
        config,
      );
      setChats(
        chats.map((c) =>
          c._id === chatId || c.chatId === chatId
            ? { ...c, title: trimmed }
            : c,
        ),
      );
      showSuccess("Debate title updated successfully");
    } catch (error) {
      console.error("Error updating debate title:", error);
      showError("Failed to update debate title. Please try again.");
    }
  };

  // Move debate to a project (used by Add to project dialog)
  const addToProject = async (chatId, projectId) => {
    const chat = chats.find((c) => c._id === chatId || c.chatId === chatId);
    if (!chat) {
      showError("Debate not found");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const config = { withCredentials: true };
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const chatIdToUpdate = chat.chatId || chat._id;
      await axios.put(
        `${API_URL}/ai/chats/${chatIdToUpdate}`,
        { projectId: projectId || null },
        config,
      );
      setChats(
        chats.map((c) =>
          c._id === chatId || c.chatId === chatId
            ? { ...c, projectId: projectId || null }
            : c,
        ),
      );
      showSuccess(
        projectId ? "Debate added to project" : "Debate removed from project",
      );
    } catch (error) {
      console.error("Error updating debate project:", error);
      showError("Failed to update project. Please try again.");
    }
  };

  const handleCreateProject = async (name) => {
    // Create a debate-mode project
    const newProject = await createProject(name, "debate");
    showSuccess("Project created");

    const newProjectId = newProject ? (newProject._id || newProject.id) : null;
    if (newProjectId) {
      setSelectedProjectId(newProjectId);
      try {
        localStorage.setItem("debateSelectedProjectId", newProjectId);
      } catch (e) {
        // ignore
      }
    }

    if (currentChatId && newProjectId) {
      const shouldMove = await confirm({
        title: "Move Debate to Project",
        message: `Would you like to move the current active debate into the new project "${name}"?`,
        confirmText: "Move Debate",
        cancelText: "No, Keep Here",
        type: "info",
      });
      if (shouldMove) {
        await addToProject(currentChatId, newProjectId);
      }
    }

    const list = await fetchProjects("debate");
    setProjects(list || []);
    fetchChats();
  };

  // ── Project Management Handlers (matches Research Mode) ──

  const handleRenameProject = async (projectId, newName) => {
    try {
      await updateProject(projectId, newName);
      setProjects((prev) =>
        prev.map((p) => (p._id === projectId ? { ...p, name: newName } : p))
      );
      showSuccess("Project renamed");
    } catch (err) {
      console.error("Error renaming project:", err);
      showError(err?.response?.data?.message || "Failed to rename project", "Rename Error");
    }
  };

  const handleArchiveProject = async (project) => {
    const isCurrentlyArchived = !!project.archived;
    try {
      await archiveProject(project._id, !isCurrentlyArchived);
      if (!isCurrentlyArchived) {
        setProjects((prev) => prev.filter((p) => p._id !== project._id));
        if (selectedProjectId === project._id) {
          setSelectedProjectId(null);
          try { localStorage.removeItem("debateSelectedProjectId"); } catch (e) { /* ignore */ }
        }
        showSuccess(`"${project.name}" archived`);
      } else {
        const list = await fetchProjects("debate");
        setProjects(list || []);
        showSuccess(`"${project.name}" restored`);
      }
    } catch (err) {
      console.error("Error archiving project:", err);
      showError(err?.response?.data?.message || "Failed to archive project", "Archive Error");
    }
  };

  const handleDeleteProject = async (project) => {
    if (!project) return;

    const confirmed = await confirm({
      title: "Delete Project",
      message: `Are you sure you want to permanently delete "${project.name}"? Chats inside this project will be moved to All Chats and not deleted. This action cannot be undone.`,
      confirmText: "Delete Project",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) return;

    setDeletingProjectId(project._id);
    try {
      await deleteProject(project._id);
      setProjects((prev) => prev.filter((p) => p._id !== project._id));
      if (selectedProjectId === project._id) {
        setSelectedProjectId(null);
        try { localStorage.removeItem("debateSelectedProjectId"); } catch (e) { /* ignore */ }
      }
      showSuccess(`"${project.name}" deleted`);
      fetchChats(); // chats have been moved to uncategorized
    } catch (err) {
      console.error("Error deleting project:", err);
      showError(err?.response?.data?.message || "Failed to delete project", "Delete Error");
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Close projects dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        projectsDropdownRef.current &&
        !projectsDropdownRef.current.contains(e.target)
      ) {
        setProjectsDropdownOpen(false);
      }
    };
    if (projectsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside, true);
    }
  }, [projectsDropdownOpen]);

  // Group debate chats by project for sidebar (chats with no project first with no label, then each project)
  const projectSections = [
    { key: "none", label: "", chats: chats.filter((c) => !c.projectId) },
  ];
  projects.forEach((p) => {
    projectSections.push({
      key: p._id,
      label: p.name,
      chats: chats.filter((c) => c.projectId === p._id),
    });
  });

  const chatsForSelectedProject =
    selectedProjectId != null
      ? chats.filter((c) => c.projectId === selectedProjectId)
      : null;
  const selectedProjectName = selectedProjectId
    ? projects.find((p) => p._id === selectedProjectId)?.name || "Project"
    : null;

  // Handle delete chat
  const deleteChat = async (chatId, e) => {
    if (e) {
      e.stopPropagation();
    }

    const confirmed = await confirm({
      title: "Delete Debate",
      message:
        "Are you sure you want to delete this debate? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) {
      return;
    }

    try {
      setDeletingChatId(chatId);
      const token = localStorage.getItem("token");
      const config = {
        withCredentials: true,
      };
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      await axios.delete(`${API_URL}/ai/chats/${chatId}`, config);

      setChats(chats.filter((chat) => chat._id !== chatId));
      if (debateListCache.userId === user?.userId) {
        debateListCache = {
          ...debateListCache,
          chats: debateListCache.chats.filter((c) => c._id !== chatId),
        };
      }

      // Navigate to new debate if deleting current chat
      const deletedChat = chats.find((c) => c._id === chatId);
      const deletedChatId16 =
        deletedChat?.chatId16 || deletedChat?.chatId || chatId;
      if (chatId === deletedChatId16 || chatId === currentChatId) {
        navigate("/debate-mode", { replace: true });
      }
    } catch (error) {
      console.error("Error deleting debate:", error);
      showError("Failed to delete debate");
      throw error;
    } finally {
      setDeletingChatId(null);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(" ");
      if (names.length >= 2)
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      return user.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return (
    <motion.aside
      animate={{
        width: sidebarOpen
          ? isMediumScreen
            ? "30vw"
            : isSmallScreen && !isMobileScreen
              ? "20vw"
              : isMobileScreen
                ? "40vw"
                : "16vw"
          : isSmallScreen
            ? "10vw"
            : "5vw",
        x: sidebarOpen ? 0 : isSidebarOverlay ? "-100%" : 0,
        opacity: 1,
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`sidebar ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      initial={{ x: isSidebarOverlay ? "-100%" : 0, opacity: 0 }}
      style={{
        width: sidebarOpen
          ? isMediumScreen
            ? "30vw"
            : isSmallScreen && !isMobileScreen
              ? "20vw"
              : isMobileScreen
                ? "40vw"
                : "16vw"
          : isSmallScreen
            ? "10vw"
            : "5vw",
        minWidth: sidebarOpen
          ? isMediumScreen
            ? "30vw"
            : isSmallScreen && !isMobileScreen
              ? "20vw"
              : isMobileScreen
                ? "40vw"
                : "16vw"
          : isSmallScreen
            ? "10vw"
            : "5vw",
        maxWidth: sidebarOpen
          ? isMediumScreen
            ? "30vw"
            : isSmallScreen && !isMobileScreen
              ? "20vw"
              : isMobileScreen
                ? "40vw"
                : "16vw"
          : isSmallScreen
            ? "10vw"
            : "5vw",
        height: `calc(100dvh - ${resolvedHeaderHeightPx}px)`,
        overflowX: "hidden",
        overflowY: "auto",
        position: "fixed",
        left: 0,
        top: `${resolvedHeaderHeightPx}px`,
        zIndex: isMobileScreen ? 1100 : 900,
      }}
    >
      <div
        className="sidebar-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          paddingTop: 0,
          marginTop: 0,
          height: "100%",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <motion.nav
          className="sidebar-nav"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 0%",
            minHeight: 0,
            overflow: "visible",
            paddingTop: "0.5rem",
          }}
        >
          {/* Collapse button - at top when collapsed, in row with New Debate when open */}
          {!sidebarOpen && (
            <motion.div
              variants={itemVariants}
              style={{ marginBottom: "0.5rem" }}
            >
              <button
                className="sidebar-toggle-btn sidebar-collapse-btn-sidebar"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding:
                    "clamp(0.4vh, 0.375rem, 0.8vh) clamp(0.5vh, 0.5rem, 1vh)",
                  color: darkMode ? "#cbd5e1" : "#1e3a8a",
                  borderRadius: "clamp(0.6vh, 0.75rem, 1.2vh)",
                  minHeight: "clamp(3vh, 2.5rem, 4.5vh)",
                  width: "100%",
                }}
                title="Open sidebar"
              >
                <PanelLeft size={20} />
              </button>
            </motion.div>
          )}

          {/* New Debate - with collapse button in same row when open */}
          <motion.div
            variants={itemVariants}
            style={{
              display: sidebarOpen ? "flex" : "block",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <div
              className="sidebar-item"
              onClick={(e) => {
                if (e) e.stopPropagation();
                handleNewDebateClick();
              }}
              style={{
                cursor: "pointer",
                flex: sidebarOpen ? 1 : "none",
                margin: 0,
              }}
              title={!sidebarOpen ? "New Debate" : undefined}
            >
              <MessageSquare size={20} />
              {sidebarOpen && (
                <span className="sidebar-item-label">New Debate</span>
              )}
            </div>
            {sidebarOpen && (
              <button
                className="sidebar-toggle-btn sidebar-collapse-btn-sidebar"
                onClick={(e) => {
                  if (e) e.stopPropagation();
                  setSidebarOpen(!sidebarOpen);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  color: darkMode ? "#cbd5e1" : "#1e3a8a",
                  borderRadius: "0.5rem",
                  minWidth: "44px",
                  minHeight: "44px",
                  flexShrink: 0,
                }}
                title="Close sidebar"
              >
                <PanelLeftClose size={20} />
              </button>
            )}
          </motion.div>

          {isLoggedIn && (
            <motion.div variants={itemVariants} style={{ flexShrink: 0 }}>
              <motion.div
                className="sidebar-item"
                onPointerDown={(e) => {
                  if (isMobileScreen) {
                    e.stopPropagation();
                    e.preventDefault();
                    setSearchDialogLoaded(true);
                    setIsSearchOpen(true);
                  }
                }}
                onClick={(e) => {
                  if (!isMobileScreen) {
                    setSearchDialogLoaded(true);
                    setIsSearchOpen(true);
                  }
                }}
                style={{ cursor: "pointer" }}
                title={(!sidebarOpen && !isMobileScreen) ? "Search Chats" : undefined}
              >
                <Search size={20} />
                {sidebarOpen && (
                  <span className="sidebar-item-label">Search Chats</span>
                )}
              </motion.div>
            </motion.div>
          )}

          {isLoggedIn && (
            <div
              ref={projectsDropdownRef}
              className="sidebar-projects-wrap"
              style={{ position: "relative", flexShrink: 0 }}
            >
              <motion.div variants={itemVariants}>
                <motion.div
                  className="sidebar-item"
                  onPointerDown={(e) => {
                    if (isMobileScreen) {
                      e.preventDefault();
                      handleSidebarProjectsClick(e);
                    }
                  }}
                onClick={(e) => {
                  if (!isMobileScreen) {
                    handleSidebarProjectsClick(e);
                  }
                }}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  width: "100%",
                }}
                title={(!sidebarOpen && !isMobileScreen) ? "New Project" : undefined}
              >
                <FolderPlus size={20} />
                {sidebarOpen && (
                  <>
                    <span
                      className="sidebar-item-label"
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedProjectName || "Projects"}
                    </span>
                    <ChevronDown
                      size={16}
                      style={{
                        transform: projectsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        flexShrink: 0,
                        opacity: 0.8
                      }}
                    />
                  </>
                )}
                </motion.div>
              </motion.div>
              <AnimatePresence>
                {sidebarOpen && projectsDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "0.5rem",
                      right: "0.5rem",
                      backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                      border: `1px solid ${darkMode ? "#334155" : "#e2e8f0"}`,
                      borderRadius: "0.75rem",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      zIndex: 1000,
                      padding: "0.5rem",
                      marginTop: "0.25rem",
                      maxHeight: "300px",
                      overflowY: "auto"
                    }}
                  >
                    <div
                      className={`sidebar-chat-item ${selectedProjectId === null ? "sidebar-chat-item-active" : ""}`}
                      onClick={() => {
                        handleSelectProject(null);
                        setProjectsDropdownOpen(false);
                      }}
                      style={{ padding: "0.5rem", borderRadius: "0.5rem", marginBottom: "0.25rem", cursor: "pointer" }}
                    >
                      <p style={{ fontWeight: 700, fontSize: "0.875rem", margin: 0 }}>All Chats</p>
                    </div>
                    {projects.map((p) => (
                      <div
                        key={p._id}
                        className={`sidebar-chat-item ${selectedProjectId === p._id ? "sidebar-chat-item-active" : ""}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0.5rem",
                          borderRadius: "0.5rem",
                          marginBottom: "0.25rem"
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            if (e) e.stopPropagation();
                            handleSelectProject(p._id);
                            setProjectsDropdownOpen(false);
                          }}
                          style={{
                            flex: 1,
                            cursor: "pointer",
                            overflow: "hidden",
                            background: "transparent",
                            border: "none",
                            textAlign: "left",
                            padding: 0,
                            color: "inherit"
                          }}
                        >
                          <p style={{ fontWeight: 700, fontSize: "0.875rem", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                        </button>
                        <div 
                          className="chat-menu-container"
                          data-menu-open={projectContextMenu?.project?._id === p._id}
                        >
                          <button
                            type="button"
                            className="project-menu-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setProjectContextMenu({
                                project: p,
                                x: rect.right + 4,
                                y: rect.top,
                              });
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0.25rem",
                              display: "flex",
                              alignItems: "center",
                              color: "inherit",
                            }}
                          >
                            <MoreHorizontal size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div
                      className="sidebar-item"
                      onClick={() => { setShowNewProjectModal(true); setProjectsDropdownOpen(false); }}
                      style={{ padding: "0.5rem", borderRadius: "0.5rem", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Plus size={16} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>New Project</span>
                    </div>
                    <div
                      className="sidebar-item"
                      onClick={() => { setShowArchivedProjects(true); setProjectsDropdownOpen(false); }}
                      style={{ padding: "0.5rem", borderRadius: "0.5rem", cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Archive size={16} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Archived</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {isLoggedIn && sidebarOpen && (
            <motion.div
              className="sidebar-divider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ margin: "1rem 0" }}
            >
              <p className="sidebar-chats-label">Chats</p>
            </motion.div>
          )}

          {isLoggedIn
            ? sidebarOpen && (
              <div
                className="sidebar-chats-list"
                ref={chatListRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overscrollBehaviorY: "contain",
                  WebkitOverflowScrolling: "touch",
                  touchAction: "pan-y",
                }}
              >
                {loadingChats ? (
                  [...Array(5)].map((_, i) => <SidebarChatSkeleton key={i} />)
                ) : selectedProjectId != null ? (
                  chatsForSelectedProject?.length === 0 ? (
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: darkMode ? "#94a3b8" : "#374151",
                        fontSize: "0.875rem",
                      }}
                    >
                      No chats in this project
                    </div>
                  ) : (
                    (chatsForSelectedProject || []).map((chat) => {
                      const chatId16 =
                        chat.chatId16 || chat.chatId || chat._id;
                      const isActive =
                        currentChatId === chat._id ||
                        currentChatId === chatId16;
                      const rerunCount =
                        parseInt(chat?.rerunCount || 0, 10) || 0;
                      const hasReruns = rerunCount > 0;
                      const isExplicitInitial =
                        isActive && activeRunSelection?.run === "initial";
                      const isExplicitRerun =
                        isActive && !!activeRunSelection?.rerun;
                      const isImplicitLatest =
                        isActive && !isExplicitInitial && !isExplicitRerun;
                      const isParentHighlighted =
                        isExplicitInitial || (!hasReruns && isImplicitLatest);
                      const isDeleting =
                        deletingChatId === chat._id ||
                        deletingChatId === chat.chatId ||
                        deletingChatId === chatId16;
                      const isDebateLoading =
                        (chat.chatStatus === "loading" ||
                          chat.rerunInProgress) &&
                        (isActive || !chat.debateRounds?.length);
                      const isDebateStopped = chat.chatStatus === "stopped";
                      return (
                        <React.Fragment key={chat._id}>
                          <motion.div
                            className={`sidebar-chat-item ${isParentHighlighted ? "sidebar-chat-item-active" : ""} ${isDeleting ? "sidebar-chat-item-deleting" : ""}`}
                            whileHover={
                              !isDeleting
                                ? {
                                  backgroundColor: isParentHighlighted
                                    ? "rgba(37, 99, 235, 0.35)"
                                    : darkMode
                                      ? "rgba(71, 85, 105, 0.6)"
                                      : "rgba(191, 219, 254, 0.7)",
                                  borderColor: isParentHighlighted
                                    ? "rgba(37, 99, 235, 0.6)"
                                    : darkMode
                                      ? "rgba(148, 163, 184, 0.5)"
                                      : "rgba(147, 197, 253, 0.8)",
                                }
                                : {}
                            }
                            onClick={() => {
                              if (!isDeleting) {
                                handleChatSelect(chat.chatId || chat._id);
                              }
                            }}
                            style={{
                              backgroundColor: isParentHighlighted
                                ? "rgba(37, 99, 235, 0.15)"
                                : "rgba(219, 234, 254, 0)",
                              border: isParentHighlighted
                                ? "1px solid rgba(37, 99, 235, 0.3)"
                                : "1px solid rgba(219, 234, 254, 0)",
                              position: "relative",
                              overflow: "visible",
                              padding: "0.5rem",
                              borderRadius: "0.5rem",
                              cursor: isDeleting ? "wait" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "0.3rem",
                              marginBottom: "0.25rem",
                              opacity: isDeleting ? 0.8 : 1,
                            }}
                          >
                            <div
                              className="chat-text-wrapper"
                              style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              {isDeleting ? (
                                <>
                                  <Loader
                                    size={18}
                                    style={{
                                      flexShrink: 0,
                                      animation: "spin 1s linear infinite",
                                    }}
                                  />
                                  <span
                                    style={{
                                      flex: 1,
                                      fontSize: "0.875rem",
                                      color: darkMode ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    Deleting…
                                  </span>
                                </>
                              ) : (
                                <>
                                  {isDebateLoading ? (
                                    <Loader
                                      size={16}
                                      style={{
                                        flexShrink: 0,
                                        animation: "spin 1s linear infinite",
                                        color: darkMode ? "#94a3b8" : "#2563eb",
                                      }}
                                    />
                                  ) : isDebateStopped ? (
                                    <XCircle
                                      size={16}
                                      style={{ flexShrink: 0, color: "#ef4444" }}
                                    />
                                  ) : (
                                    <Check
                                      size={16}
                                      style={{ flexShrink: 0, color: "#22c55e" }}
                                    />
                                  )}
                                  <span
                                    title={
                                      (chat.title && chat.title.trim()) ||
                                      (chat.query && chat.query.trim()) ||
                                      "Untitled Debate"
                                    }
                                    style={{
                                      flex: 1,
                                      fontSize: "0.875rem",
                                      fontWeight: "bold",
                                      color: isParentHighlighted
                                        ? "#2563eb"
                                        : darkMode
                                          ? "#f1f5f9"
                                          : "#1e3a8a",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {getDebateTitleLabel(chat)}
                                  </span>
                                </>
                              )}
                            </div>
                            <div
                              className="chat-menu-container"
                              data-menu-open={openMenuId === chat._id}
                              style={{
                                position: "relative",
                                marginLeft: "0.125rem",
                                flexShrink: 0,
                              }}
                            >
                              <motion.button
                                className="chat-menu-button"
                                data-chat-id={chat._id}
                                onClick={(e) =>
                                  !isDeleting && handleMenuToggle(chat._id, e)
                                }
                                disabled={isDeleting}
                                whileHover={
                                  !isDeleting
                                    ? {
                                      scale: 1.1,
                                      backgroundColor: darkMode
                                        ? "#334155"
                                        : "rgba(191, 219, 254, 0.5)",
                                    }
                                    : {}
                                }
                                whileTap={!isDeleting ? { scale: 0.9 } : {}}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: isDeleting ? "wait" : "pointer",
                                  padding: "0.125rem",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: darkMode ? "#94a3b8" : "#1e3a8a",
                                  borderRadius: "4px",
                                  transition: "background-color 0.2s",
                                  opacity: isDeleting ? 0.5 : 1,
                                }}
                                title={
                                  isDeleting ? "Deleting…" : "More options"
                                }
                              >
                                <MoreHorizontal size={16} />
                              </motion.button>
                            </div>
                          </motion.div>
                          {Array.from(
                            { length: Math.max(0, chat?.rerunCount || 0) },
                            (_, idx) => idx + 1,
                          ).map((n) => {
                            const isChildActive =
                              isActive &&
                              (activeRunSelection?.rerun === n ||
                                (isImplicitLatest && n === rerunCount));
                            // Get rerun date if available
                            const rerunDates = chat?.rerunDates || [];
                            const rerunDate = rerunDates[n - 1];
                            const dateLabel = rerunDate
                              ? (() => {
                                const d = new Date(rerunDate);
                                if (isNaN(d.getTime())) return "";
                                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                return ` — ${months[d.getMonth()]} ${d.getDate()}`;
                              })()
                              : "";
                            return (
                              <motion.div
                                key={`${chat._id}-rerun-${n}`}
                                className={`sidebar-chat-item ${isChildActive ? "sidebar-chat-item-active" : ""}`}
                                whileHover={{
                                  backgroundColor: isChildActive
                                    ? "rgba(37, 99, 235, 0.15)"
                                    : darkMode
                                      ? "rgba(71, 85, 105, 0.45)"
                                      : "rgba(191, 219, 254, 0.5)",
                                }}
                                onClick={() => {
                                  if (!isDeleting) {
                                    navigate(
                                      `/debate-mode/${chatId16}?rerun=${n}`,
                                    );
                                    if (isMobileScreen && sidebarOpen) {
                                      setSidebarOpen(false);
                                    }
                                  }
                                }}
                                style={{
                                  marginLeft: "1.75rem",
                                  marginTop: "0.15rem",
                                  padding: "0.45rem 0.5rem",
                                  borderRadius: "0.5rem",
                                  cursor: isDeleting ? "wait" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  border: "1px solid rgba(219, 234, 254, 0)",
                                  opacity: isDeleting ? 0.8 : 1,
                                }}
                                title={`Open Revised ReRun-${n}${dateLabel}`}
                              >
                                <span
                                  style={{
                                    flex: 1,
                                    fontSize: "0.8rem",
                                    color: isChildActive
                                      ? "#2563eb"
                                      : darkMode
                                        ? "#cbd5e1"
                                        : "#1e3a8a",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {`Revised ReRun-${n}${dateLabel}`}
                                </span>
                              </motion.div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })
                  )
                ) : chats.length === 0 ? (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: darkMode ? "#94a3b8" : "#374151",
                      fontSize: "0.875rem",
                    }}
                  >
                    No chats yet
                  </div>
                ) : (
                  projectSections.map((section) =>
                    section.chats.length === 0 ? null : (
                      <div key={section.key} style={{ marginBottom: "1rem" }}>
                        {section.label ? (
                          <p
                            style={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: darkMode ? "#94a3b8" : "#64748b",
                              marginBottom: "0.5rem",
                              paddingLeft: "0.5rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {section.label}
                          </p>
                        ) : null}
                        {section.chats.map((chat) => {
                          const chatId16 =
                            chat.chatId16 || chat.chatId || chat._id;
                          const isActive =
                            currentChatId === chat._id ||
                            currentChatId === chatId16;
                          const rerunCount =
                            parseInt(chat?.rerunCount || 0, 10) || 0;
                          const hasReruns = rerunCount > 0;
                          const isExplicitInitial =
                            isActive && activeRunSelection?.run === "initial";
                          const isExplicitRerun =
                            isActive && !!activeRunSelection?.rerun;
                          const isImplicitLatest =
                            isActive &&
                            !isExplicitInitial &&
                            !isExplicitRerun;
                          const isParentHighlighted =
                            isExplicitInitial ||
                            (!hasReruns && isImplicitLatest);
                          const isDeleting =
                            deletingChatId === chat._id ||
                            deletingChatId === chat.chatId ||
                            deletingChatId === chatId16;
                          const isDebateLoading =
                            (chat.chatStatus === "loading" ||
                              chat.rerunInProgress) &&
                            (isActive || !chat.debateRounds?.length);
                          const isDebateStopped = chat.chatStatus === "stopped";
                          return (
                            <React.Fragment key={chat._id}>
                              <motion.div
                                className={`sidebar-chat-item ${isParentHighlighted ? "sidebar-chat-item-active" : ""} ${isDeleting ? "sidebar-chat-item-deleting" : ""}`}
                                whileHover={
                                  !isDeleting
                                    ? {
                                      backgroundColor: isParentHighlighted
                                        ? "rgba(37, 99, 235, 0.2)"
                                        : "rgba(191, 219, 254, 0.3)",
                                    }
                                    : {}
                                }
                                onClick={() => {
                                  if (!isDeleting) {
                                    handleChatSelect(chat.chatId || chat._id);
                                  }
                                }}
                                style={{
                                  backgroundColor: isParentHighlighted
                                    ? "rgba(37, 99, 235, 0.15)"
                                    : "rgba(219, 234, 254, 0)",
                                  border: isParentHighlighted
                                    ? "1px solid rgba(37, 99, 235, 0.3)"
                                    : "1px solid rgba(219, 234, 254, 0)",
                                  position: "relative",
                                  overflow: "visible",
                                  padding: "0.5rem",
                                  borderRadius: "0.5rem",
                                  cursor: isDeleting ? "wait" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "0.3rem",
                                  marginBottom: "0.25rem",
                                  opacity: isDeleting ? 0.8 : 1,
                                }}
                              >
                                <div
                                  className="chat-text-wrapper"
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.75rem",
                                  }}
                                >
                                  {isDeleting ? (
                                    <>
                                      <Loader
                                        size={18}
                                        style={{
                                          flexShrink: 0,
                                          animation:
                                            "spin 1s linear infinite",
                                        }}
                                      />
                                      <span
                                        style={{
                                          flex: 1,
                                          fontSize: "0.875rem",
                                          color: darkMode
                                            ? "#94a3b8"
                                            : "#64748b",
                                        }}
                                      >
                                        Deleting…
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      {isDebateLoading ? (
                                        <Loader
                                          size={16}
                                          style={{
                                            flexShrink: 0,
                                            animation: "spin 1s linear infinite",
                                            color: darkMode ? "#94a3b8" : "#2563eb",
                                          }}
                                        />
                                      ) : isDebateStopped ? (
                                        <XCircle
                                          size={16}
                                          style={{ flexShrink: 0, color: "#ef4444" }}
                                        />
                                      ) : (
                                        <Check
                                          size={16}
                                          style={{ flexShrink: 0, color: "#22c55e" }}
                                        />
                                      )}
                                      <span
                                        title={
                                          (chat.title && chat.title.trim()) ||
                                          (chat.query && chat.query.trim()) ||
                                          "Untitled Debate"
                                        }
                                        style={{
                                          flex: 1,
                                          fontSize: "0.875rem",
                                          fontWeight: "bold",
                                          color: isParentHighlighted
                                            ? "#2563eb"
                                            : darkMode
                                              ? "#f1f5f9"
                                              : "#1e3a8a",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {getDebateTitleLabel(chat)}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div
                                  className="chat-menu-container"
                                  data-menu-open={openMenuId === chat._id}
                                  style={{
                                    position: "relative",
                                    marginLeft: "0.125rem",
                                    flexShrink: 0,
                                  }}
                                >
                                  <motion.button
                                    className="chat-menu-button"
                                    data-chat-id={chat._id}
                                    onClick={(e) =>
                                      !isDeleting &&
                                      handleMenuToggle(chat._id, e)
                                    }
                                    disabled={isDeleting}
                                    whileHover={
                                      !isDeleting
                                        ? {
                                          scale: 1.1,
                                          backgroundColor: darkMode
                                            ? "#334155"
                                            : "rgba(191, 219, 254, 0.5)",
                                        }
                                        : {}
                                    }
                                    whileTap={
                                      !isDeleting ? { scale: 0.9 } : {}
                                    }
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      cursor: isDeleting ? "wait" : "pointer",
                                      padding: "0.125rem",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: darkMode ? "#94a3b8" : "#1e3a8a",
                                      borderRadius: "4px",
                                      transition: "background-color 0.2s",
                                      opacity: isDeleting ? 0.5 : 1,
                                    }}
                                    title={
                                      isDeleting
                                        ? "Deleting…"
                                        : "More options"
                                    }
                                  >
                                    <MoreHorizontal size={16} />
                                  </motion.button>
                                </div>
                              </motion.div>
                              {Array.from(
                                { length: Math.max(0, rerunCount) },
                                (_, idx) => idx + 1,
                              ).map((n) => {
                                const isChildActive =
                                  isActive &&
                                  (activeRunSelection?.rerun === n ||
                                    (isImplicitLatest && n === rerunCount));
                                const rerunDates2 = chat?.rerunDates || [];
                                const rerunDate2 = rerunDates2[n - 1];
                                const dateLabel2 = rerunDate2
                                  ? (() => {
                                    const d = new Date(rerunDate2);
                                    if (isNaN(d.getTime())) return "";
                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                    return ` — ${months[d.getMonth()]} ${d.getDate()}`;
                                  })()
                                  : "";
                                return (
                                  <motion.div
                                    key={`${chat._id}-rerun-${n}`}
                                    className={`sidebar-chat-item ${isChildActive ? "sidebar-chat-item-active" : ""}`}
                                    whileHover={{
                                      backgroundColor: isChildActive
                                        ? "rgba(37, 99, 235, 0.15)"
                                        : darkMode
                                          ? "rgba(71, 85, 105, 0.45)"
                                          : "rgba(191, 219, 254, 0.5)",
                                    }}
                                    onClick={() => {
                                      if (!isDeleting) {
                                        navigate(
                                          `/debate-mode/${chatId16}?rerun=${n}`,
                                        );
                                        if (isMobileScreen && sidebarOpen) {
                                          setSidebarOpen(false);
                                        }
                                      }
                                    }}
                                    style={{
                                      marginLeft: "1.75rem",
                                      marginTop: "0.15rem",
                                      padding: "0.45rem 0.5rem",
                                      borderRadius: "0.5rem",
                                      cursor: isDeleting ? "wait" : "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.5rem",
                                      border:
                                        "1px solid rgba(219, 234, 254, 0)",
                                      opacity: isDeleting ? 0.8 : 1,
                                    }}
                                    title={`Open Revised ReRun-${n}${dateLabel2}`}
                                  >
                                    <span
                                      style={{
                                        flex: 1,
                                        fontSize: "0.8rem",
                                        color: isChildActive
                                          ? "#2563eb"
                                          : darkMode
                                            ? "#cbd5e1"
                                            : "#1e3a8a",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {`Revised ReRun-${n}${dateLabel2}`}
                                    </span>
                                  </motion.div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ),
                  )
                )}
                {/* Progressive loading skeletons for paginated debates */}
                {loadingMore && debatesHasMore && (
                  <div style={{ padding: "0.25rem 0.5rem 0.5rem 0.5rem" }}>
                    {[...Array(3)].map((_, index) => (
                      <SidebarChatSkeleton
                        key={`debate-load-more-skeleton-${index}`}
                      />
                    ))}
                  </div>
                )}

              </div>
            )
            : sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  padding: "1.5rem 1rem",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    background: darkMode
                      ? "rgba(59, 130, 246, 0.1)"
                      : "#eff6ff",
                    color: darkMode ? "#60a5fa" : "#3b82f6",
                    padding: "1rem",
                    borderRadius: "50%",
                    marginBottom: "1rem",
                  }}
                >
                  <MessageSquare size={32} />
                </div>
                <h3
                  style={{
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                    marginBottom: "0.5rem",
                  }}
                >
                  Unlock More Power
                </h3>
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: darkMode ? "#94a3b8" : "#64748b",
                    marginBottom: "1.5rem",
                    lineHeight: "1.5",
                  }}
                >
                  More AI models. Extended debate rounds. Executive summaries.
                  Saved sessions.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                    width: "100%",
                  }}
                >
                  <Link
                    to="/pricing"
                    style={{
                      background: "linear-gradient(135deg, #193CB9 0%, #193CB9 100%)",
                      color: "white",
                      padding: "0.6rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      textDecoration: "none",
                      width: "100%",
                    }}
                  >
                    Start Free Trial
                  </Link>
                </div>
              </motion.div>
            )}
        </motion.nav>

        <div className="sidebar-footer">
          {sidebarOpen && isLoggedIn && (
            <motion.div
              className="usage-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p className="usage-title">AI Actions</p>

              <p className="usage-text">
                AI Actions Used: {(aiUsage.aiActionsUsed || 0).toLocaleString()} /{" "}
                {(aiUsage.aiMonthlyLimit || 0).toLocaleString()}
              </p>
              <div className="usage-progress-bar">
                <motion.div
                  className="usage-progress-fill"
                  initial={{ width: 0 }}
                  animate={{
                    width: `${(aiUsage.aiMonthlyLimit || 0) > 0
                      ? Math.min(
                        ((aiUsage.aiActionsUsed || 0) /
                          (aiUsage.aiMonthlyLimit || 0)) *
                        100,
                        100,
                      )
                      : (aiUsage.tokenLimit || 2000000) > 0
                        ? Math.min(
                          ((aiUsage.totalTokensUsed || 0) /
                            (aiUsage.tokenLimit || 2000000)) *
                          100,
                          100,
                        )
                        : 0
                      }%`,
                  }}
                  transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                ></motion.div>
              </div>
            </motion.div>
          )}

          {/* Trial upgrade CTA */}
          {sidebarOpen && isLoggedIn && user?.subscriptionStatus === "trialing" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              style={{
                margin: "0.5rem 0.75rem 0",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                background: darkMode
                  ? "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(26,61,190,0.1))"
                  : "linear-gradient(135deg, rgba(37,99,235,0.08), rgba(26,61,190,0.04))",
                border: darkMode
                  ? "1px solid rgba(59,130,246,0.25)"
                  : "1px solid rgba(37,99,235,0.15)",
              }}
            >
              <p style={{
                margin: 0,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: darkMode ? "#93c5fd" : "#1e40af",
              }}>
                Trial ends {user?.trialEnd
                  ? `in ${Math.max(0, Math.ceil((new Date(user.trialEnd) - new Date()) / (1000 * 60 * 60 * 24)))} days`
                  : "soon"}
              </p>
              <button
                onClick={() => navigate("/pricing")}
                style={{
                  marginTop: "0.5rem",
                  width: "100%",
                  padding: "0.4rem 0",
                  borderRadius: "0.375rem",
                  border: "none",
                  background: "#2563eb",
                  color: "#ffffff",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.target.style.background = "#1d4ed8"}
                onMouseLeave={(e) => e.target.style.background = "#2563eb"}
              >
                Upgrade Now
              </button>
            </motion.div>
          )}

          {sidebarOpen && isLoggedIn && (
            <div className="account-card">
              <div
                className="account-header"
                onClick={() => setAccountOpen(!accountOpen)}
              >
                <div className="account-info">
                  <div className="account-avatar">{renderUserAvatar()}</div>
                  <div>
                    <p className="account-name">{user?.name || "User"}</p>
                    <p className="account-plan">{user?.subscriptionStatus === "trialing" ? "Free Trial" : (user?.subscriptionStatus !== "active" && (user?.adminAllocatedActionsTotal || 0) > 0) ? "Admin Invited" : user?.subscriptionPlan ? `${user.subscriptionPlan.charAt(0).toUpperCase() + user.subscriptionPlan.slice(1)} Plan` : "Free Plan"}</p>
                  </div>
                </div>
                <motion.span
                  animate={{ rotate: accountOpen ? 90 : 0 }}
                  className="account-arrow"
                >
                  ▸
                </motion.span>
              </div>

              <AnimatePresence>
                {accountOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="account-menu"
                  >
                    {[
                      "💡 Request a Feature",
                      "🐞 Report a Bug",
                      "📧 Contact Us",
                    ].map((item) => (
                      <Link
                        to={`mailto:support@lawfusion.ai?subject=${item}`}
                        className="account-menu-item"
                        key={item}
                      >
                        {item}
                      </Link>
                    ))}
                    <button
                      className="account-menu-item logout-btn"
                      onClick={handleLogout}
                    >
                      ↪️ Log Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Settings Link */}
          {isLoggedIn && (
            <Link
              to="/settings"
              style={{
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <motion.div
                className="theme-toggle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{ cursor: "pointer" }}
                whileHover={{
                  x: 3,
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 400, damping: 17 },
                }}
                whileTap={{ scale: 0.98 }}
                title={!sidebarOpen ? "Settings" : undefined}
              >
                {sidebarOpen && (
                  <motion.span
                    className="theme-label"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    whileHover={{
                      x: 2,
                      color: darkMode ? "#60a5fa" : "#1d4ed8",
                      transition: { duration: 0.2 },
                    }}
                  >
                    Settings
                  </motion.span>
                )}
                <motion.div
                  className="theme-toggle-btn"
                  whileHover={{
                    rotate: 90,
                    scale: 1.1,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      damping: 15,
                    },
                  }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Settings size={18} />
                </motion.div>
              </motion.div>
            </Link>
          )}

          <div className="theme-toggle mobile-hide-theme-toggle" title={!sidebarOpen ? (darkMode ? "Light Mode" : "Dark Mode") : undefined}>
            {sidebarOpen && (
              <span className="theme-label">
                {darkMode ? "Light Mode" : "Dark Mode"}
              </span>
            )}
            <button className="theme-toggle-btn" onClick={toggleDarkMode}>
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Search Dialog - Lazy loaded when opened */}
      {isSearchOpen &&
        searchDialogLoaded &&
        typeof document !== "undefined" &&
        document.body
        ? createPortal(
          <div
            className="search-dialog-overlay"
            onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery("");
            }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999999,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              paddingTop: "10vh",
              pointerEvents: "auto",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2 }}
              className="search-dialog"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: darkMode
                  ? "rgba(30, 41, 59, 0.95)"
                  : "white",
                borderRadius: "0.75rem",
                width: "90%",
                maxWidth: "640px",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                boxShadow:
                  "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
                overflow: "hidden",
                border: darkMode ? "1px solid #475569" : "1px solid #bfdbfe",
              }}
            >
              {/* Search Input Header */}
              <div
                style={{
                  padding: "1.5rem 1rem 0 1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Search
                      size={20}
                      style={{
                        position: "absolute",
                        left: "0",
                        color: darkMode ? "#94a3b8" : "#1e3a8a",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search debate chats..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.75rem 0.75rem 0.75rem 2rem",
                        border: "none",
                        borderRadius: "0",
                        fontSize: "1rem",
                        outline: "none",
                        backgroundColor: "transparent",
                        color: darkMode ? "#f1f5f9" : "#1e3a8a",
                        fontFamily: "inherit",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && filteredChats.length > 0) {
                          const firstChat = filteredChats[0];
                          handleChatSelect(firstChat.chatId || firstChat._id);
                        }
                      }}
                    />
                    {searchQuery && (
                      <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery("");
                          searchInputRef.current?.focus();
                        }}
                        style={{
                          position: "absolute",
                          right: "0",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "0.25rem",
                          display: "flex",
                          alignItems: "center",
                          color: darkMode ? "#94a3b8" : "#1e3a8a",
                        }}
                        whileHover={{
                          scale: 1.1,
                          color: darkMode ? "#cbd5e1" : "#1e40af",
                        }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X size={16} />
                      </motion.button>
                    )}
                  </div>
                  <motion.button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      color: darkMode ? "#94a3b8" : "#1e3a8a",
                      borderRadius: "0.25rem",
                    }}
                    whileHover={{
                      backgroundColor: darkMode
                        ? "#334155"
                        : "rgba(191, 219, 254, 0.5)",
                      scale: 1.1,
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X size={20} />
                  </motion.button>
                </div>
                {/* Full width line below input */}
                <div
                  style={{
                    height: "1px",
                    backgroundColor: darkMode ? "#475569" : "#93c5fd",
                    marginLeft: "-1rem",
                    marginRight: "-1rem",
                    width: "calc(100% + 2rem)",
                  }}
                />
                {/* New Debate option below line */}
                <motion.div
                  onClick={() => {
                    handleNewDebateClick({ closeSearch: true });
                  }}
                  whileHover={{
                    backgroundColor: darkMode
                      ? "#334155"
                      : "rgba(191, 219, 254, 0.5)",
                  }}
                  style={{
                    padding: "0.75rem",
                    marginTop: "0.5rem",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <MessageSquare
                    size={18}
                    style={{
                      color: darkMode ? "#94a3b8" : "#1e3a8a",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.875rem",
                      color: darkMode ? "#f1f5f9" : "#1e3a8a",
                    }}
                  >
                    New Chat
                  </span>
                </motion.div>
              </div>

              {/* Chat List */}
              <div
                className="search-dialog-chat-list"
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "0.5rem",
                }}
              >
                {loadingChats ? (
                  <div style={{ padding: "0.5rem" }}>
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "0.75rem",
                          marginBottom: "0.25rem",
                          borderRadius: "0.5rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        <SkeletonLoader
                          width="18px"
                          height="18px"
                          style={{ borderRadius: "4px", flexShrink: 0 }}
                        />
                        <SkeletonLoader width="70%" height="0.875rem" />
                      </div>
                    ))}
                  </div>
                ) : filteredChats.length === 0 ? (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: darkMode ? "#94a3b8" : "#374151",
                      fontSize: "0.875rem",
                    }}
                  >
                    {searchQuery
                      ? "No debate chats found"
                      : "No debate chats yet"}
                  </div>
                ) : (
                  // Show filtered chats grouped by project
                  (() => {
                    const sections = [
                      {
                        key: "none",
                        label: "Private / No Project",
                        chats: filteredChats.filter((c) => !c.projectId),
                      },
                      ...projects.map((p) => ({
                        key: p._id,
                        label: p.name,
                        chats: filteredChats.filter(
                          (c) => c.projectId === p._id,
                        ),
                      })),
                    ];

                    return sections.map(
                      (section) =>
                        section.chats.length > 0 && (
                          <div
                            key={section.key}
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <h3
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: darkMode ? "#94a3b8" : "#2563eb",
                                textTransform: "uppercase",
                                letterSpacing: "0.08em",
                                marginBottom: "0.75rem",
                                padding: "0 0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <Folder size={14} />
                              {section.label}
                            </h3>
                            {section.chats.map((chat) => (
                              <motion.div
                                key={chat._id}
                                whileHover={{
                                  backgroundColor: darkMode
                                    ? "#334155"
                                    : "rgba(191, 219, 254, 0.5)",
                                }}
                                onClick={() => {
                                  handleChatSelect(chat.chatId || chat._id);
                                }}
                                style={{
                                  padding: "0.75rem",
                                  borderRadius: "0.5rem",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  marginBottom: "0.25rem",
                                }}
                              >
                                <MessageSquare
                                  size={18}
                                  style={{
                                    color: darkMode ? "#94a3b8" : "#1e3a8a",
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    flex: 1,
                                    fontSize: "0.875rem",
                                    color: darkMode ? "#f1f5f9" : "#1e3a8a",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <span style={{ fontWeight: 600 }}>
                                    {getDebateTitleLabel(chat)}
                                  </span>
                                </span>
                              </motion.div>
                            ))}
                          </div>
                        ),
                    );
                  })()
                )}
              </div>
            </motion.div>
          </div>,
          document.body,
        )
        : null}

      {/* Menu Dropdown Portal */}
      {openMenuId &&
        menuPosition[openMenuId] &&
        typeof document !== "undefined" &&
        document.body
        ? createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -5 }}
              transition={{ duration: 0.15 }}
              className="chat-menu-dropdown-portal"
              style={{
                position: "fixed",
                top: `${menuPosition[openMenuId].top}px`,
                left: `${menuPosition[openMenuId].left}px`,
                backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                border: `1px solid ${darkMode ? "#475569" : "#e5e7eb"}`,
                borderRadius: "0.5rem",
                boxShadow: darkMode
                  ? "0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 4px 10px -2px rgba(0, 0, 0, 0.3)"
                  : "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 4px 10px -2px rgba(0, 0, 0, 0.1)",
                zIndex: 999999,
                minWidth: "120px",
                maxWidth: "150px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                pointerEvents: "auto",
              }}
            >
              <motion.button
                type="button"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setRenameDialogChatId(openMenuId);
                  setRenameDialogAnchor(menuPosition[openMenuId] || null);
                  setTimeout(() => setOpenMenuId(null), 50);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                whileHover={{
                  backgroundColor: darkMode ? "#334155" : "#f3f4f6",
                  x: 2,
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  color: darkMode ? "#cbd5e1" : "#374151",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                }}
                title="Rename debate"
              >
                <Pencil size={16} style={{ flexShrink: 0 }} />
                <span>Rename</span>
              </motion.button>
              <motion.button
                type="button"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setAddToProjectDialogChatId(openMenuId);
                  setAddToProjectDialogAnchor(
                    menuPosition[openMenuId] || null,
                  );
                  setTimeout(() => setOpenMenuId(null), 50);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                whileHover={{
                  backgroundColor: darkMode ? "#334155" : "#f3f4f6",
                  x: 2,
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  color: darkMode ? "#cbd5e1" : "#374151",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                }}
                title="Add to project"
              >
                <FolderPlus size={16} style={{ flexShrink: 0 }} />
                <span>Add to project</span>
              </motion.button>
              <div
                style={{
                  height: "1px",
                  background: darkMode
                    ? "linear-gradient(to right, transparent, #475569, transparent)"
                    : "linear-gradient(to right, transparent, #e5e7eb, transparent)",
                  margin: "0 0.5rem",
                }}
              />
              <motion.button
                type="button"
                onMouseDown={async (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const chatIdToDelete = openMenuId;
                  if (!chatIdToDelete) return;
                  // Close menu first so only the confirmation dialog remains visible.
                  setOpenMenuId(null);
                  try {
                    await deleteChat(chatIdToDelete, e);
                  } catch (error) {
                    console.error("Error in delete action:", error);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                whileHover={{
                  backgroundColor: darkMode ? "#7f1d1d" : "#fee2e2",
                  x: 2,
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: "100%",
                  padding: "0.625rem 1rem",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  color: darkMode ? "#f87171" : "#ef4444",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                }}
                title="Delete debate"
              >
                <Trash2 size={16} style={{ flexShrink: 0 }} />
                <span>Delete</span>
              </motion.button>
            </motion.div>
          </AnimatePresence>,
          document.body,
        )
        : null}

      <RenameChatDialog
        open={!!renameDialogChatId}
        anchorRect={renameDialogAnchor}
        chatId={renameDialogChatId}
        currentTitle={
          renameDialogChatId
            ? chats.find((c) => c._id === renameDialogChatId)?.title ||
            chats.find((c) => c._id === renameDialogChatId)?.query ||
            "Untitled Debate"
            : ""
        }
        onSave={(chatId, newTitle) => {
          saveRename(chatId, newTitle);
          setRenameDialogChatId(null);
          setRenameDialogAnchor(null);
        }}
        onClose={() => {
          setRenameDialogChatId(null);
          setRenameDialogAnchor(null);
        }}
        darkMode={darkMode}
        label="Debate"
      />
      <AddToProjectDialog
        open={!!addToProjectDialogChatId}
        anchorRect={addToProjectDialogAnchor}
        chatId={addToProjectDialogChatId}
        currentProjectId={
          addToProjectDialogChatId
            ? (chats.find((c) => c._id === addToProjectDialogChatId)
              ?.projectId ?? null)
            : null
        }
        projects={projects}
        onSelect={(chatId, projectId) => {
          addToProject(chatId, projectId);
          setAddToProjectDialogChatId(null);
          setAddToProjectDialogAnchor(null);
        }}
        onClose={() => {
          setAddToProjectDialogChatId(null);
          setAddToProjectDialogAnchor(null);
        }}
        darkMode={darkMode}
        label="Debate"
      />
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onCreate={handleCreateProject}
        existingProjects={projects}
        darkMode={darkMode}
      />

      {/* Project context menu (Rename / Archive / Delete) */}
      <ProjectContextMenu
        open={!!projectContextMenu}
        position={projectContextMenu ? { x: projectContextMenu.x, y: projectContextMenu.y } : { x: 0, y: 0 }}
        project={projectContextMenu?.project}
        onRename={(project) => setRenameProjectDialog(project)}
        onArchive={(project) => handleArchiveProject(project)}
        onDelete={(project) => handleDeleteProject(project)}
        onClose={() => setProjectContextMenu(null)}
        darkMode={darkMode}
      />

      {/* Rename project dialog */}
      <RenameProjectDialog
        open={!!renameProjectDialog}
        project={renameProjectDialog}
        existingProjects={projects}
        onSave={handleRenameProject}
        onClose={() => setRenameProjectDialog(null)}
        darkMode={darkMode}
      />

      {/* Archived projects view */}
      <ArchivedProjectsView
        open={showArchivedProjects}
        onClose={() => setShowArchivedProjects(false)}
        onProjectRestored={async () => {
          const list = await fetchProjects("debate");
          setProjects(list || []);
        }}
        darkMode={darkMode}
      />
    </motion.aside>
  );
};

export default DebateSidebar;
